import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import AuthShell from '../components/layout/AuthShell';
import { Eye, EyeSlash, CircleNotch } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../lib/utils';

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const st = location.state || {};
    if (st.emailVerifiedOk) {
      setSuccessMsg(
        st.accountCreatedOk
          ? t('auth.verifyEmailSuccessAccountReady', {
              defaultValue:
                'Your email is verified and your account is ready. Sign in with your email and password.',
            })
          : t('auth.verifyEmailSuccess', {
              defaultValue: 'Email verified successfully. You can sign in now.',
            }),
      );
      if (st.verifiedEmail) setEmail(String(st.verifiedEmail));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.welcomeBack')}
      description={t('auth.signInDesc')}
      footer={(
        <>
          <p className="text-center text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link
              to="/register/company"
              className="font-medium text-brand-600 hover:text-brand-700"
              data-testid="register-link"
            >
              {t('auth.createAccount')}
            </Link>
          </p>
          <p className="mt-3 text-center">
            <Link to="/pricing" className="text-sm font-medium text-muted-foreground hover:text-brand-600">
              View pricing plans →
            </Link>
          </p>
        </>
      )}
    >
      {successMsg && (
        <div
          className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200"
          data-testid="login-verify-success"
        >
          {successMsg}
        </div>
      )}
      {error && (
        <div
          className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
          data-testid="login-error"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">{t('common.email')}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            data-testid="login-email-input"
            autoComplete="email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{t('common.password')}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="login-password-input"
              className="pr-11"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted-foreground hover:text-foreground"
              data-testid="toggle-password-btn"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            {t('auth.forgotPasswordLink')}
          </Link>
        </div>

        <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full">
          {loading ? <CircleNotch className="h-5 w-5 animate-spin" /> : t('auth.signIn')}
        </Button>
      </form>
    </AuthShell>
  );
}
